import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendar, FaClock, FaUser, FaArrowLeft, FaShareAlt } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getBrand } from '../utils/brandStyles';
import config from '../config';

const ArticleDetail = ({ market, content }) => {
  const b = getBrand(market);
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${config.apiUrl}/articles/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error('Article not found');
        return res.json();
      })
      .then(data => {
        setArticle(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading article:', err);
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateReadTime = (text) => {
    return Math.ceil(text.split(' ').length / 200);
  };

  const shareArticle = () => {
    if (navigator.share) {
      navigator.share({ title: article.title, text: article.excerpt, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div style={{ fontFamily: b.fontBody, background: b.bg, color: b.text, padding: '100px 20px', textAlign: 'center' }}>
        <p style={{ color: b.textMuted, fontSize: b.fz.lg }}>Loading article…</p>
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/insights" replace />;
  }

  const ctaLink = market === 'lu' ? '/services/assessment' : '/contact';

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── BACK LINK ── */}
      <div style={{ padding: '28px clamp(24px, 6vw, 80px) 0' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <Link to="/insights" style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            color: b.accent, textDecoration: 'none', fontWeight: 600,
            fontSize: b.fz.xs, letterSpacing: b.isMWO ? '0.04em' : 'normal',
            fontFamily: b.isMWO ? "'Barlow Condensed', sans-serif" : b.fontBody,
            textTransform: b.isMWO ? 'uppercase' : 'none'
          }}>
            <FaArrowLeft size={12} /> Back to Insights
          </Link>
        </div>
      </div>

      {/* ── ARTICLE HEADER ── */}
      <motion.section
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        style={{ padding: '36px clamp(24px, 6vw, 80px) 0', maxWidth: '860px', margin: '0 auto' }}
      >
        {/* Category badge */}
        <div style={{
          display: 'inline-block',
          background: b.isMWO ? 'rgba(0,188,212,0.12)' : '#EFF6FF',
          color: b.accent, padding: '5px 14px',
          borderRadius: b.isMWO ? '0' : '16px',
          fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: '20px'
        }}>
          {article.category}
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: b.fontDisplay,
          fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
          color: b.textHeading,
          marginBottom: '18px',
          lineHeight: 1.15,
          letterSpacing: b.isMWO ? '-0.01em' : '-0.02em'
        }}>
          {article.title}
        </h1>

        {/* Excerpt */}
        <p style={{ fontSize: b.fz.lg, lineHeight: 1.7, color: b.textMuted, marginBottom: '24px' }}>
          {article.excerpt}
        </p>

        {/* Meta row */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '20px',
          paddingBottom: '20px',
          borderBottom: `1px solid ${b.isMWO ? 'rgba(232,228,220,0.12)' : '#f1f5f9'}`,
          marginBottom: '36px', fontSize: b.fz.xs, color: b.textMuted
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaUser size={12} /> {article.author || (content && content.teamName) || 'Mind IT Team'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaCalendar size={12} /> {formatDate(article.publishedDate)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaClock size={12} /> {calculateReadTime(article.content)} min read
          </span>
          <button onClick={shareArticle} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            color: b.accent, background: 'none', border: 'none',
            cursor: 'pointer', fontSize: b.fz.xs, fontWeight: 600,
            fontFamily: b.fontBody
          }}>
            <FaShareAlt size={12} /> Share
          </button>
        </div>

        {/* Featured image */}
        {article.image && (
          <div style={{ marginBottom: '40px', borderRadius: b.cardRadius, overflow: 'hidden' }}>
            <img src={article.image} alt={article.title}
              style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }} />
          </div>
        )}

        {/* Article body */}
        <div style={{ fontSize: b.fz.lg, lineHeight: 1.85, color: b.text }} className="markdown-content">
          {/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 style={{ fontFamily: b.fontDisplay, fontSize: '1.9rem', marginTop: '40px', marginBottom: '18px', color: b.textHeading, letterSpacing: '-0.02em' }} {...props} />,
              h2: ({node, ...props}) => <h2 style={{ fontFamily: b.fontDisplay, fontSize: '1.5rem', marginTop: '36px', marginBottom: '14px', color: b.textHeading, letterSpacing: '-0.02em' }} {...props} />,
              h3: ({node, ...props}) => <h3 style={{ fontFamily: b.fontDisplay, fontSize: '1.2rem', marginTop: '28px', marginBottom: '12px', color: b.textHeading }} {...props} />,
              h4: ({node, ...props}) => <h4 style={{ fontSize: '1rem', marginTop: '22px', marginBottom: '10px', color: b.text, fontWeight: 700 }} {...props} />,
              p: ({node, ...props}) => <p style={{ marginBottom: '20px' }} {...props} />,
              ul: ({node, ...props}) => <ul style={{ marginBottom: '20px', marginLeft: '20px' }} {...props} />,
              ol: ({node, ...props}) => <ol style={{ marginBottom: '20px', marginLeft: '20px' }} {...props} />,
              li: ({node, ...props}) => <li style={{ marginBottom: '8px' }} {...props} />,
              strong: ({node, ...props}) => <strong style={{ fontWeight: 700, color: b.textHeading }} {...props} />,
              em: ({node, ...props}) => <em style={{ fontStyle: 'italic' }} {...props} />,
              blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: `3px solid ${b.accent}`, paddingLeft: '20px', marginLeft: 0, marginBottom: '20px', color: b.textMuted, fontStyle: 'italic' }} {...props} />,
              code: ({node, inline, ...props}) => inline
                ? <code style={{ background: b.isMWO ? '#1e2330' : '#f8fafc', padding: '2px 6px', borderRadius: '3px', fontSize: '0.88em', fontFamily: 'monospace', color: b.isMWO ? b.accent : '#0F1B45' }} {...props} />
                : <code style={{ display: 'block', background: b.isMWO ? '#1e2330' : '#f8fafc', padding: '16px', borderRadius: b.cardRadius, marginBottom: '20px', fontSize: '0.88em', fontFamily: 'monospace', overflowX: 'auto', color: b.text }} {...props} />,
              a: ({node, ...props}) => <a style={{ color: b.accent, textDecoration: 'none', borderBottom: `1px solid ${b.accent}` }} {...props} />
            }}
          >
            {article.content}
          </ReactMarkdown>
          {/* eslint-enable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
        </div>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div style={{ marginTop: '40px', paddingTop: '28px', borderTop: `1px solid ${b.isMWO ? 'rgba(232,228,220,0.1)' : '#f1f5f9'}` }}>
            <h4 style={{ fontSize: b.fz.xs, color: b.textMuted, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Tags
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {article.tags.map((tag, idx) => (
                <span key={idx} style={{
                  background: b.isMWO ? '#1e2330' : '#f1f5f9',
                  color: b.textMuted, padding: '5px 12px',
                  borderRadius: b.isMWO ? '0' : '12px',
                  fontSize: b.fz.xs
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </motion.section>

      {/* spacing before CTA */}
      <div style={{ height: 'clamp(40px, 6vw, 80px)' }} />

      {/* ── CTA ── */}
      <section style={{
        background: b.bgCTA,
        padding: b.secPad,
        textAlign: b.isMWO ? 'left' : 'center'
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          {b.isMWO && <div style={{ width: '40px', height: '3px', background: b.accent, marginBottom: '24px' }} />}
          <h2 style={{
            fontFamily: b.fontDisplay,
            fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)',
            color: '#ffffff',
            marginBottom: '14px',
            letterSpacing: b.isMWO ? '-0.01em' : '-0.02em'
          }}>
            {market === 'lu' ? 'Ready to transform your IT?' : "Let's work together"}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '28px', fontSize: b.fz.base, lineHeight: 1.7, maxWidth: b.isMWO ? '480px' : '560px', margin: b.isMWO ? '0 0 28px' : '0 auto 28px' }}>
            {market === 'lu'
              ? 'Get a free IT assessment and discover how we can help your business.'
              : 'Contact us to discuss your IT strategy and digital transformation needs.'}
          </p>
          <Link to={ctaLink} className={b.ctaBtnClass}>Get Started</Link>
        </div>
      </section>
    </div>
  );
};

export default ArticleDetail;
